import React, { useState } from 'react';
import NumberProperty from './NumberProperty';
import PropertyBase from './PropertyBase';
import GizmoControllerProperty from './GizmoControllerProperty';
import Engine from '../../classes/Engine';
import { ChainLinkClosedIcon, ChainLinkOpenedIcon, IconFillStyles} from '../Iconset';
import Button from '../layout/Button';

export default function Vector3Property(props)
{
	const [isLinked, setIsLinked] = useState(props.canBeLinked !== undefined ? props.canBeLinked : false);

	const multivalues = props.multivalues ? props.multivalues : {};
	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-vector3-property";

	function onXChanged(value)
	{
		if (props.onChangeCallback)
		{
			let finalValue = {
				x: Number(value),
				y: props.value[1],
				z: props.value[2]
			}

			// WC20-1825: aspect ratio between xy and z remains respected (eg. x=1, y=2, z=2, changing x to 2 would result in x=2, y=4, z=4)
			const ratio = isLinked ? (value / props.value[0]) : 1.0;

			if (ratio !== Infinity)
			{
				finalValue.y *= ratio;
				finalValue.z *= ratio;
			}
			else
			{			
				if(!finalValue.y) finalValue.y = Number(value);
				if(!finalValue.z) finalValue.z = Number(value);
			}

			if(props.unitScaling)
			{
				finalValue.y *= Engine.getInstance().getDisplayUnitScale();
				finalValue.z *= Engine.getInstance().getDisplayUnitScale();
			}

			if(multivalues.y) finalValue.y = '~';
			if(multivalues.z) finalValue.z = '~';
			
			props.onChangeCallback(finalValue);
		}
	}

	function onYChanged(value)
	{
		if (props.onChangeCallback)
		{
			let finalValue = {
				x: props.value[0],
				y: Number(value),
				z: props.value[2]
			}

			// WC20-1825: aspect ratio between xy and z remains respected (eg. x=1, y=2, z=2, changing x to 2 would result in x=2, y=4, z=4)
			const ratio = isLinked ? (value / props.value[1]) : 1.0;

			if (ratio !== Infinity)
			{
				finalValue.x *= ratio;
				finalValue.z *= ratio;
			}
			else
			{			
				if(!finalValue.x) finalValue.x = Number(value);
				if(!finalValue.z) finalValue.z = Number(value);
			}

			if(props.unitScaling)
			{
				finalValue.x *= Engine.getInstance().getDisplayUnitScale();
				finalValue.z *= Engine.getInstance().getDisplayUnitScale();
			}

			if(multivalues.x) finalValue.x = '~';
			if(multivalues.z) finalValue.z = '~';
			
			props.onChangeCallback(finalValue);
		}
	}

	function onZChanged(value)
	{
		if (props.onChangeCallback)
		{
			let finalValue = {
				x: props.value[0],
				y: props.value[1],
				z: Number(value)
			}

			// WC20-1825: aspect ratio between xy and z remains respected (eg. x=1, y=2, z=2, changing x to 2 would result in x=2, y=4, z=4)
			const ratio = isLinked ? (value / props.value[2]) : 1.0;

			if (ratio !== Infinity)
			{
				finalValue.x *= ratio;
				finalValue.y *= ratio;
			}
			else
			{			
				if(!finalValue.x) finalValue.x = Number(value);
				if(!finalValue.y) finalValue.y = Number(value);
			}

			if(props.unitScaling)
			{
				finalValue.x *= Engine.getInstance().getDisplayUnitScale();
				finalValue.y *= Engine.getInstance().getDisplayUnitScale();
			}

			if(multivalues.x) finalValue.x = '~';
			if(multivalues.y) finalValue.y = '~';
			
			props.onChangeCallback(finalValue);
		}
	}

	function onValueCommitted(val, label)
	{
		const fScaling = props.unitScaling? Engine.getInstance().getDisplayUnitScale() : 1.0; //Scaling is already performed in the NumberProperty commit

		const newValues = {
			x: props.value[0] * fScaling,
			y: props.value[1] * fScaling,
			z: props.value[2] * fScaling
		};

		if (label !== undefined) {newValues[label] = val;}
		props.onCommitCallback(newValues);
	}

	function onFieldBlur()
	{
		if (props.onBlur)
			props.onBlur();
	}

	return (
		<PropertyBase {...props}
			data-name={dataName + "-root"}
			renderChildrenInSeparateRows={props.renderChildrenInSeparateRows}
			addons={[
				!props.canBeLinked ? null :
					<Button
						key={dataName + "-link-button"}
						className={"addon"}
						data-name={dataName + "-toggle-value-link"}
						iconRenderer={isLinked ? <ChainLinkClosedIcon scale={0.75} fillStyle={IconFillStyles.yellow} /> : <ChainLinkOpenedIcon scale={0.75}/>}
						onClick={() => setIsLinked(!isLinked)}
					/>,
			]}
		>
			<NumberProperty
				data-name={dataName + "-subcomponent-x"}
				compositeLabel='x'
				compositeLabelClass="red"
				value={props.value[0]}
				multivalues={multivalues.x}
				readOnly={props.readOnly}
				step={props.step}
				unitScaling={props.unitScaling}
				onChangeCallback={onXChanged}
				onCommitCallback={onValueCommitted}
				defaultValue={props.defaultValues ? props.defaultValue.x : undefined}
				onBlur={onFieldBlur}
				onFocus={props.onFocus}
				decimals={props.decimals}
				contextMenuOverride={props.contextMenuOverride}
			/>
			<NumberProperty
				data-name={dataName + "-subcomponent-y"}
				compositeLabel='y'
				compositeLabelClass="green"
				value={props.value[1]}
				multivalues={multivalues.y}
				readOnly={props.readOnly}
				step={props.step}
				unitScaling={props.unitScaling}
				onChangeCallback={onYChanged}
				onCommitCallback={onValueCommitted}
				defaultValue={props.defaultValues ? props.defaultValue.y : undefined}
				onBlur={onFieldBlur}
				onFocus={props.onFocus}
				decimals={props.decimals}
				contextMenuOverride={props.contextMenuOverride}
			/>
			<NumberProperty
				data-name={dataName + "-subcomponent-z"}
				compositeLabel='z'
				compositeLabelClass="blue"
				value={props.value[2]}
				multivalues={multivalues.z}
				readOnly={props.readOnly}
				step={props.step}
				unitScaling={props.unitScaling}
				onChangeCallback={onZChanged}
				onCommitCallback={onValueCommitted}
				defaultValue={props.defaultValues ? props.defaultValue.z : undefined}
				onBlur={onFieldBlur}
				onFocus={props.onFocus}
				decimals={props.decimals}
				contextMenuOverride={props.contextMenuOverride}
			/>
			{
				!props.enableGizmoController ? null :
					<GizmoControllerProperty {...props}
						cellclass={"propertySmallCell"}
						gizmoControlMouseUpCallback={props.gizmoControlMouseUpCallback ?? props.onCommitCallback}
					>
					</GizmoControllerProperty>
			}
		</PropertyBase>
	);
}
